import * as THREE from "three";
import { dom } from "../dom";
import { type SavedPlanView, type SavedView, VIEWS_STORAGE_PREFIX, state } from "../state";
import { camera, controls, getActiveCamera, planCamera, renderer, scene } from "../viewer/scene";
import { applyClipping, syncClippingUiFromState } from "./clipping";
import { applyPlanBookmark, capturePlanBookmark, exitPlanView } from "./planView";
import { rebuildMeshVisibility } from "../ifc/filters";
import { applyColorRule, clearColorRule, syncColorRuleUi } from "./colorRules";
import { setStatus } from "../ui/status";
import { t } from "../i18n";
import { escapeHtmlAttr, escapeHtmlText } from "../utils/html";

function readSavedPlan(raw: unknown): SavedPlanView | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const plan = raw as Partial<SavedPlanView>;
  const vec = (value: unknown): [number, number, number] | null =>
    Array.isArray(value) && value.length >= 3 && value.slice(0, 3).every((n) => Number.isFinite(Number(n)))
      ? [Number(value[0]), Number(value[1]), Number(value[2])]
      : null;
  const position = vec(plan.position);
  const target = vec(plan.target);
  const up = vec(plan.up);
  if (!position || !target || !up) {
    return null;
  }
  return {
    storeyName: String(plan.storeyName || ""),
    cutAxis: Number(plan.cutAxis) || 0,
    cut: Number(plan.cut) || 0,
    zoom: Number.isFinite(Number(plan.zoom)) ? Number(plan.zoom) : 1,
    fitHalf: Number.isFinite(Number(plan.fitHalf)) ? Number(plan.fitHalf) : 10,
    position,
    target,
    up,
  };
}

function viewsStorageKey(fileKey: string): string {
  return `${VIEWS_STORAGE_PREFIX}${fileKey}`;
}

export function persistViews(): void {
  if (!state.currentIfcFileKey) {
    return;
  }
  try {
    localStorage.setItem(viewsStorageKey(state.currentIfcFileKey), JSON.stringify(state.savedViews));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

export function loadViewsForCurrentFile(): void {
  state.savedViews.length = 0;
  state.viewIdCounter = 1;
  if (!state.currentIfcFileKey) {
    renderViewsList();
    return;
  }
  try {
    const raw = localStorage.getItem(viewsStorageKey(state.currentIfcFileKey));
    if (!raw) {
      renderViewsList();
      return;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      renderViewsList();
      return;
    }
    let maxId = 0;
    for (const item of parsed) {
      if (!item || typeof item !== "object") {
        continue;
      }
      const id = Number(item.id) || state.viewIdCounter++;
      maxId = Math.max(maxId, id);
      state.savedViews.push({
        id,
        name: String(item.name || `Вид ${id}`),
        thumbnail: String(item.thumbnail || ""),
        cameraPosition: Array.isArray(item.cameraPosition)
          ? (item.cameraPosition.slice(0, 3) as [number, number, number])
          : [0, 0, 0],
        cameraTarget: Array.isArray(item.cameraTarget)
          ? (item.cameraTarget.slice(0, 3) as [number, number, number])
          : [0, 0, 0],
        cameraUp: Array.isArray(item.cameraUp)
          ? (item.cameraUp.slice(0, 3) as [number, number, number])
          : [0, 1, 0],
        hiddenExpressIds: Array.isArray(item.hiddenExpressIds)
          ? (item.hiddenExpressIds as number[]).filter((n) => Number.isFinite(n))
          : [],
        filterState: item.filterState || { storeyId: "", types: [], mark: "" },
        clipping: item.clipping || {
          enabled: false,
          axisX: { enabled: false, value: 0.5 },
          axisY: { enabled: false, value: 0.5 },
          axisZ: { enabled: false, value: 0.5 },
          invertX: false,
          invertY: false,
          invertZ: false,
        },
        colorRule: item.colorRule || null,
        createdAt: Number(item.createdAt) || Date.now(),
        plan: readSavedPlan(item.plan),
      });
    }
    state.viewIdCounter = Math.max(state.viewIdCounter, maxId + 1);
  } catch (_) {
    /* corrupt storage — ignore */
  }
  renderViewsList();
}

function makeThumbnail(): string {
  try {
    // Принудительно перерисуем кадр — после анимационного цикла content canvas
    // может быть уже очищен на чтение, поэтому копируем в свой буфер сразу.
    renderer.render(scene, getActiveCamera());
    const sourceCanvas = renderer.domElement;
    const thumb = document.createElement("canvas");
    thumb.width = 192;
    thumb.height = 120;
    const ctx = thumb.getContext("2d");
    if (!ctx) {
      return sourceCanvas.toDataURL("image/jpeg", 0.5);
    }
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, thumb.width, thumb.height);
    ctx.drawImage(
      sourceCanvas,
      0,
      0,
      sourceCanvas.width,
      sourceCanvas.height,
      0,
      0,
      thumb.width,
      thumb.height,
    );
    return thumb.toDataURL("image/jpeg", 0.6);
  } catch (_) {
    return "";
  }
}

export function saveCurrentView(): void {
  if (!state.ifcModel) {
    setStatus("Сначала загрузите IFC.", true);
    return;
  }
  const nameRaw = dom.viewNameInput.value.trim();
  const name = nameRaw || `Вид ${state.viewIdCounter}`;
  const plan = state.planView.active ? capturePlanBookmark() : null;
  const viewCamera = plan ? planCamera : camera;
  const view: SavedView = {
    id: state.viewIdCounter++,
    name,
    thumbnail: makeThumbnail(),
    cameraPosition: [viewCamera.position.x, viewCamera.position.y, viewCamera.position.z],
    cameraTarget: plan ? plan.target : [controls.target.x, controls.target.y, controls.target.z],
    cameraUp: [viewCamera.up.x, viewCamera.up.y, viewCamera.up.z],
    hiddenExpressIds: [...state.hiddenExpressIds],
    filterState: {
      storeyId: state.activeFilters.storeyId,
      types: [...state.activeFilters.types],
      mark: state.activeFilters.mark,
    },
    clipping: JSON.parse(JSON.stringify(state.clipping)),
    colorRule: state.activeColorRule
      ? JSON.parse(JSON.stringify(state.activeColorRule))
      : null,
    createdAt: Date.now(),
    plan,
  };
  state.savedViews.push(view);
  persistViews();
  renderViewsList();
  dom.viewNameInput.value = "";
  setStatus(`Сохранён вид «${view.name}».`);
}

export function deleteSavedView(id: number): void {
  const i = state.savedViews.findIndex((v) => v.id === id);
  if (i === -1) {
    return;
  }
  state.savedViews.splice(i, 1);
  persistViews();
  renderViewsList();
}

export function applySavedView(id: number): void {
  const view = state.savedViews.find((v) => v.id === id);
  if (!view || !state.ifcModel) {
    return;
  }
  state.hiddenExpressIds.clear();
  for (const id of view.hiddenExpressIds) {
    state.hiddenExpressIds.add(id);
  }

  state.activeFilters.storeyId = view.filterState.storeyId || "";
  state.activeFilters.types = [...(view.filterState.types || [])];
  state.activeFilters.mark = view.filterState.mark || "";
  dom.filterStoreySelect.value = state.activeFilters.storeyId;
  const typesSet = new Set(state.activeFilters.types);
  [...dom.filterTypeSelect.options].forEach((opt) => {
    opt.selected = typesSet.has(opt.value);
  });
  dom.filterMarkInput.value = state.activeFilters.mark;

  state.clipping = JSON.parse(JSON.stringify(view.clipping));
  syncClippingUiFromState();

  if (view.plan) {
    applyPlanBookmark(view.plan);
  } else {
    if (state.planView.active) {
      exitPlanView({ restoreVisibility: false });
    }
    camera.position.set(view.cameraPosition[0], view.cameraPosition[1], view.cameraPosition[2]);
    camera.up.set(view.cameraUp[0], view.cameraUp[1], view.cameraUp[2]);
    controls.target.set(view.cameraTarget[0], view.cameraTarget[1], view.cameraTarget[2]);
    controls.update();
    applyClipping();
    rebuildMeshVisibility();
  }

  if (view.colorRule && view.colorRule.mode !== "none") {
    state.activeColorRule = view.colorRule;
    applyColorRule();
  } else {
    clearColorRule();
  }
  syncColorRuleUi();

  setStatus(`Применён вид «${view.name}».`);
}

export function renderViewsList(): void {
  const hasModel = Boolean(state.ifcModel);
  dom.viewNameInput.disabled = !hasModel;
  dom.viewSaveButton.disabled = !hasModel;
  if (state.savedViews.length === 0) {
    dom.viewsListElement.innerHTML =
      '<p class="muted" style="margin:0; font-size:12px;">Видов пока нет.</p>';
    return;
  }
  const cards = state.savedViews
    .slice()
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((view) => {
      const dateText = new Date(view.createdAt).toLocaleString("ru-RU");
      const thumb = view.thumbnail
        ? `<img class="view-card-thumb" src="${escapeHtmlAttr(view.thumbnail)}" alt="thumb" />`
        : '<div class="view-card-thumb"></div>';
      return (
        `<div class="view-card" data-view-id="${view.id}">` +
        thumb +
        `<div><div class="view-card-title">${escapeHtmlText(view.name)}</div>` +
        `<div class="view-card-date">${escapeHtmlText(dateText)}${view.plan ? ` · ${escapeHtmlText(t("views.planBadge"))}` : ""}</div></div>` +
        `<div class="view-card-actions">` +
        `<button type="button" data-view-apply="${view.id}">Открыть</button>` +
        `<button type="button" data-view-delete="${view.id}">×</button>` +
        `</div></div>`
      );
    })
    .join("");
  dom.viewsListElement.innerHTML = cards;
}

export function handleViewsListClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const applyBtn = target.closest<HTMLElement>("[data-view-apply]");
  if (applyBtn) {
    const id = Number(applyBtn.dataset.viewApply);
    if (Number.isFinite(id)) {
      applySavedView(id);
    }
    return;
  }
  const deleteBtn = target.closest<HTMLElement>("[data-view-delete]");
  if (deleteBtn) {
    const id = Number(deleteBtn.dataset.viewDelete);
    if (Number.isFinite(id)) {
      deleteSavedView(id);
    }
  }
}

// silence unused warning when imported by main but used only via interaction
void THREE;
